import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Crop, RotateCcw, Check, Wand2 } from "lucide-react";

/**
 * Lightweight square photo cropper.
 * Props: open, onClose, imageUrl (blob/data URL), onCropped(dataUrl)
 * Renders a 320x320 viewport; user pans (drag) and zooms (slider).
 * On confirm draws the visible square into a 512x512 canvas → JPEG data URL.
 */
const VIEW = 320;
const OUT = 512;

export default function PhotoCropperDialog({ open, onClose, imageUrl, onCropped }) {
  const [img, setImg] = useState(null); // HTMLImageElement
  const [scale, setScale] = useState(1);
  const [minScale, setMinScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const draggingRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!open || !imageUrl) return;
    const im = new Image();
    im.crossOrigin = "anonymous";
    im.onload = () => {
      // Compute min scale so the image fully covers the square viewport
      const s = Math.max(VIEW / im.naturalWidth, VIEW / im.naturalHeight);
      setImg(im);
      setMinScale(s);
      setScale(s);
      // Center the image inside viewport
      setOffset({
        x: (VIEW - im.naturalWidth * s) / 2,
        y: (VIEW - im.naturalHeight * s) / 2,
      });
    };
    im.src = imageUrl;
  }, [open, imageUrl]);

  const clampOffset = (nx, ny, s) => {
    const w = img.naturalWidth * s;
    const h = img.naturalHeight * s;
    // Keep image covering the viewport
    const minX = VIEW - w;
    const minY = VIEW - h;
    const x = Math.min(0, Math.max(minX, nx));
    const y = Math.min(0, Math.max(minY, ny));
    return { x, y };
  };

  const onScaleChange = (e) => {
    const s = Number(e.target.value);
    if (!img) return;
    // Zoom around center of viewport
    const cx = VIEW / 2, cy = VIEW / 2;
    const ratio = s / scale;
    const nx = cx - (cx - offset.x) * ratio;
    const ny = cy - (cy - offset.y) * ratio;
    setScale(s);
    setOffset(clampOffset(nx, ny, s));
  };

  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    draggingRef.current = { startX: e.clientX, startY: e.clientY, ox: offset.x, oy: offset.y };
  };
  const onPointerMove = (e) => {
    if (!draggingRef.current || !img) return;
    const d = draggingRef.current;
    setOffset(clampOffset(d.ox + (e.clientX - d.startX), d.oy + (e.clientY - d.startY), scale));
  };
  const onPointerUp = () => { draggingRef.current = null; };

  const doCrop = () => {
    if (!img) return;
    const c = canvasRef.current || document.createElement("canvas");
    c.width = OUT; c.height = OUT;
    const ctx = c.getContext("2d");
    // Draw the same scene at higher resolution
    const k = OUT / VIEW;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, OUT, OUT);
    ctx.drawImage(
      img,
      offset.x * k,
      offset.y * k,
      img.naturalWidth * scale * k,
      img.naturalHeight * scale * k,
    );
    const dataUrl = c.toDataURL("image/jpeg", 0.9);
    onCropped?.(dataUrl);
    onClose();
  };

  const reset = () => {
    if (!img) return;
    setScale(minScale);
    setOffset({
      x: (VIEW - img.naturalWidth * minScale) / 2,
      y: (VIEW - img.naturalHeight * minScale) / 2,
    });
  };

  // Auto-center face: use native FaceDetector when available, fallback to
  // "portrait rule of thirds" (face usually sits ~35% from top).
  const autoFace = async () => {
    if (!img) return;
    let boxCx = null, boxCy = null, boxSize = null;
    try {
      if (typeof window !== "undefined" && "FaceDetector" in window) {
        const detector = new window.FaceDetector({ fastMode: true, maxDetectedFaces: 3 });
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext("2d").drawImage(img, 0, 0);
        const faces = await detector.detect(canvas);
        if (faces && faces.length) {
          // pick tallest bounding box
          faces.sort((a, b) => b.boundingBox.height - a.boundingBox.height);
          const bb = faces[0].boundingBox;
          boxCx = bb.x + bb.width / 2;
          boxCy = bb.y + bb.height / 2;
          // Expand face box so the crop includes shoulders/hair
          boxSize = Math.max(bb.width, bb.height) * 2.4;
        }
      }
    } catch (e) {
      // ignore, fall through to heuristic
    }
    // Heuristic fallback: assume face is at ~35% from top, horizontally centered
    if (boxCx == null) {
      boxCx = img.naturalWidth / 2;
      boxCy = img.naturalHeight * 0.35;
      boxSize = Math.min(img.naturalWidth, img.naturalHeight * 0.75);
    }
    // Compute scale so boxSize fits into the viewport (VIEW px)
    const targetScale = VIEW / boxSize;
    const s = Math.max(minScale, Math.min(minScale * 4, targetScale));
    // Compute offset so box center is at viewport center
    const nx = VIEW / 2 - boxCx * s;
    const ny = VIEW / 2 - boxCy * s;
    setScale(s);
    setOffset(clampOffset(nx, ny, s));
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-sm" data-testid="photo-cropper-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg font-black flex items-center gap-2">
            <Crop className="w-5 h-5 text-indigo-600" /> قص الصورة (مربّع)
          </DialogTitle>
        </DialogHeader>
        <div className="text-[11px] text-slate-500 mb-3">
          اسحب الصورة لضبط الوسط · استخدم الشريط للتكبير. الإطار الدائري المنقّط يوضح الإطار النهائي.
        </div>

        <div className="flex justify-center">
          <div
            className="relative bg-slate-100 select-none touch-none rounded-2xl overflow-hidden border-2 border-slate-200"
            style={{ width: VIEW, height: VIEW, cursor: draggingRef.current ? "grabbing" : "grab" }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            data-testid="cropper-viewport"
          >
            {img && (
              <img
                src={img.src}
                alt=""
                draggable={false}
                style={{
                  position: "absolute",
                  left: offset.x,
                  top: offset.y,
                  width: img.naturalWidth * scale,
                  height: img.naturalHeight * scale,
                  maxWidth: "none",
                  pointerEvents: "none",
                  userSelect: "none",
                }}
              />
            )}
            {/* Circle mask overlay to preview the round frame */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                boxShadow: `inset 0 0 0 4px rgba(99,102,241,0.9), inset 0 0 0 999px rgba(0,0,0,0)`,
                borderRadius: "50%",
                mixBlendMode: "normal",
              }}
            />
            <div
              className="absolute inset-2 pointer-events-none border-2 border-dashed border-indigo-500 rounded-full opacity-70"
            />
          </div>
        </div>

        <div className="mt-4">
          <label className="text-[11px] font-black text-slate-600 block mb-1">التكبير</label>
          <input
            type="range"
            min={minScale}
            max={minScale * 4}
            step={0.01}
            value={scale}
            onChange={onScaleChange}
            className="w-full accent-indigo-600"
            data-testid="cropper-zoom"
          />
        </div>

        <canvas ref={canvasRef} style={{ display: "none" }} />

        <div className="mt-4 flex items-center justify-between gap-2">
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={reset} className="gap-1" data-testid="cropper-reset">
              <RotateCcw className="w-3.5 h-3.5" /> إعادة ضبط
            </Button>
            <Button variant="outline" size="sm" onClick={autoFace} className="gap-1 text-indigo-700 border-indigo-300 bg-indigo-50" data-testid="cropper-auto-face" title="اكتشاف الوجه وتوسيطه تلقائياً">
              <Wand2 className="w-3.5 h-3.5" /> توسيط تلقائي
            </Button>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} data-testid="cropper-cancel">إلغاء</Button>
            <Button onClick={doCrop} className="gap-2 bg-gradient-to-l from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700" data-testid="cropper-confirm">
              <Check className="w-4 h-4" /> قص وحفظ
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
